import { LabelledField, Wizard, type WizardStep } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import type { FormFieldConfig } from "components/Forms/FormFieldsDefinition";
import { ValueComponent } from "components/Forms/FormValueComponents";
import { TournamentSessionFormController } from "components/Forms/TournamentSessionForm";
import WizardModal from "components/common/WizardModal";
import { injectModal } from "lib/injectParams";
import { useMemo } from "react";
import { useNavigate } from "router";

const ControlledNewTournamentWizard = () => {
  const controller = TournamentSessionFormController.use();
  const { record, loading } = controller.state;
  const fields = useMemo(() => controller.fields(), []);
  const stepFields = {
    basic: [fields.name, fields.courts],
    time: [fields.starts_at, fields.ends_at],
    deadline: [fields.registration_closes_at],
  };

  if (loading) {
    return <WizardModal />;
  }

  const getValueComponent = (field: FormFieldConfig, i: number) => (
    <LabelledField key={i} label={field.label} helpText={field.description} htmlFor={field.name}>
      <ValueComponent field={field} record={record} autoFocus={i === 0} />
    </LabelledField>
  );

  const validateFields = (fields: FormFieldConfig[]) => {
    return () =>
      fields.every((field) => {
        const value = (record as unknown as Record<string, unknown>)[field.name];
        return !field.required || value;
      });
  };

  const steps: WizardStep[] = [
    {
      name: "Basic",
      title: "Create a new tournament",
      subtitle: "Start with the essential details",
      content: <>{stepFields.basic.map(getValueComponent)}</>,
      validate: validateFields(stepFields.basic),
    },
    {
      name: "Time",
      title: "When will the tournament run?",
      subtitle: "Set the start and end times",
      content: <>{stepFields.time.map(getValueComponent)}</>,
      validate: validateFields(stepFields.time),
    },
    {
      name: "Registration",
      title: "Set registration deadline",
      subtitle: "When should entries close?",
      content: <>{stepFields.deadline.map(getValueComponent)}</>,
      validate: validateFields(stepFields.deadline),
    },
  ];

  return (
    <WizardModal>
      <Wizard steps={steps} onComplete={() => controller.actionSubmit()} doneButtonText="Create tournament" />
    </WizardModal>
  );
};
const NewTournamentWizard = TournamentSessionFormController.scope(
  ApplicationView(ControlledNewTournamentWizard),
);

const NewTournamentModal: React.FC<{ clubId: string; name: string | undefined }> = ({ clubId, name }) => {
  const navigate = useNavigate();

  return (
    <NewTournamentWizard
      onSubmit={async (session) => {
        navigate("/tournament/:id", { params: { id: session.id! } });
      }}
      attributes={{
        club_id: clubId,
        name,
      }}
    />
  );
};

export default injectModal(ApplicationView(NewTournamentModal));
